import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  EventBusy as BulkIcon,
} from '@mui/icons-material';
import { useVacations, useVacationMutations } from '../services/api';

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const parseDatesInput = (input) => {
  const raw = input
    .split(/[\n,]+/)
    .map((value) => value.trim())
    .filter(Boolean);

  const unique = [...new Set(raw)];
  const valid = unique.filter((date) => DATE_PATTERN.test(date));
  const invalid = unique.filter((date) => !DATE_PATTERN.test(date));

  return { valid, invalid };
};

const formatDisplayDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const VacationsPage = () => {
  const { data: vacations = [], isLoading } = useVacations();
  const { createMutation, bulkCreateMutation, updateMutation, deleteMutation } =
    useVacationMutations();

  const [dialogMode, setDialogMode] = useState(null);
  const [editingVacation, setEditingVacation] = useState(null);
  const [formData, setFormData] = useState({ date: '', note: '' });
  const [bulkDatesInput, setBulkDatesInput] = useState('');
  const [bulkNote, setBulkNote] = useState('');
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const sortedVacations = useMemo(
    () =>
      [...vacations].sort((a, b) =>
        String(a.date).localeCompare(String(b.date))
      ),
    [vacations]
  );

  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const resetForm = () => {
    setFormData({ date: '', note: '' });
    setBulkDatesInput('');
    setBulkNote('');
    setEditingVacation(null);
  };

  const handleCloseDialog = () => {
    setDialogMode(null);
    resetForm();
  };

  const handleOpenCreate = () => {
    resetForm();
    setDialogMode('create');
  };

  const handleOpenBulk = () => {
    resetForm();
    setDialogMode('bulk');
  };

  const handleOpenEdit = (vacation) => {
    setEditingVacation(vacation);
    setFormData({
      date: vacation.date?.substring(0, 10) ?? '',
      note: vacation.note ?? '',
    });
    setDialogMode('edit');
  };

  const handleDelete = (vacation) => {
    if (
      !window.confirm(
        `Delete vacation on ${formatDisplayDate(vacation.date)}?`
      )
    ) {
      return;
    }

    deleteMutation.mutate(vacation.id, {
      onSuccess: () => {
        showNotification('Vacation date deleted successfully');
      },
      onError: (error) => {
        showNotification(
          error.response?.data?.message || error.message || 'Failed to delete vacation',
          'error'
        );
      },
    });
  };

  const handleSubmitSingle = (event) => {
    event.preventDefault();

    if (!DATE_PATTERN.test(formData.date)) {
      showNotification('Date must be in YYYY-MM-DD format', 'error');
      return;
    }

    const payload = {
      date: formData.date,
      note: formData.note.trim() || undefined,
    };

    if (dialogMode === 'edit' && editingVacation) {
      updateMutation.mutate(
        { id: editingVacation.id, ...payload },
        {
          onSuccess: () => {
            showNotification('Vacation updated successfully');
            handleCloseDialog();
          },
          onError: (error) => {
            showNotification(
              error.response?.data?.message || error.message || 'Failed to update vacation',
              'error'
            );
          },
        }
      );
      return;
    }

    createMutation.mutate(payload, {
      onSuccess: () => {
        showNotification('Vacation date added successfully');
        handleCloseDialog();
      },
      onError: (error) => {
        showNotification(
          error.response?.data?.message || error.message || 'Failed to add vacation',
          'error'
        );
      },
    });
  };

  const handleSubmitBulk = (event) => {
    event.preventDefault();

    const { valid, invalid } = parseDatesInput(bulkDatesInput);

    if (invalid.length > 0) {
      showNotification(
        `Invalid date format: ${invalid.join(', ')}. Use YYYY-MM-DD.`,
        'error'
      );
      return;
    }

    if (valid.length === 0) {
      showNotification('Add at least one valid date', 'error');
      return;
    }

    bulkCreateMutation.mutate(
      {
        dates: valid,
        note: bulkNote.trim() || undefined,
      },
      {
        onSuccess: (result) => {
          const createdCount = result?.created?.length ?? valid.length;
          const skippedCount = result?.skipped?.length ?? 0;
          showNotification(
            `Added ${createdCount} vacation date(s)${skippedCount ? `, skipped ${skippedCount} existing` : ''}`
          );
          handleCloseDialog();
        },
        onError: (error) => {
          showNotification(
            error.response?.data?.message || error.message || 'Failed to bulk add vacations',
            'error'
          );
        },
      }
    );
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h4" component="h1">
            Vacation Dates
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<BulkIcon />}
              onClick={handleOpenBulk}
            >
              Bulk Add
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
            >
              Add Vacation
            </Button>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Users cannot select these dates for delivery. Orders on vacation dates
          are rejected with: لا يمكن اختيار هذا التاريخ للتوصيل - تاريخ عطلة
        </Typography>

        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Formatted</TableCell>
                    <TableCell>Note</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedVacations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No vacation dates configured
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedVacations.map((vacation) => (
                      <TableRow key={vacation.id} hover>
                        <TableCell>{vacation.date?.substring(0, 10)}</TableCell>
                        <TableCell>{formatDisplayDate(vacation.date)}</TableCell>
                        <TableCell>{vacation.note || '—'}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEdit(vacation)}
                            title="Edit vacation"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(vacation)}
                            title="Delete vacation"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={dialogMode === 'create' || dialogMode === 'edit'}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmitSingle}>
          <DialogTitle>
            {dialogMode === 'edit' ? 'Edit Vacation' : 'Add Vacation'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Date"
                  name="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, date: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Note"
                  name="note"
                  value={formData.note}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, note: e.target.value }))
                  }
                  placeholder="Optional note, e.g. Holiday break"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={createMutation.isLoading || updateMutation.isLoading}
            >
              {dialogMode === 'edit' ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={dialogMode === 'bulk'}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmitBulk}>
          <DialogTitle>Bulk Add Vacation Dates</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label="Dates"
                  value={bulkDatesInput}
                  onChange={(e) => setBulkDatesInput(e.target.value)}
                  placeholder={'2026-12-25\n2026-12-26\n2027-01-01'}
                  helperText="One date per line or comma-separated. Format: YYYY-MM-DD"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Note"
                  value={bulkNote}
                  onChange={(e) => setBulkNote(e.target.value)}
                  placeholder="Optional note applied to all dates"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={bulkCreateMutation.isLoading}
            >
              Add Dates
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default VacationsPage;
